import { get } from "../api/axios"
import endpoints from "../api/endpoints"
import { isMemberUser } from "./routeAccess"

const doneKey = (userId, membershipId) =>
  `amok.mandatoryAccept.done.${userId}.${membershipId}`

/** Remember a completed accept for this browser tab (survives refresh). */
export const markMandatoryProjectAcceptDone = (userId, membershipId) => {
  if (!userId || !membershipId) return
  try {
    sessionStorage.setItem(doneKey(userId, membershipId), "1")
  } catch {
    // ignore storage failures
  }
}

export const isMandatoryProjectAcceptDone = (userId, membershipId) => {
  if (!userId || !membershipId) return false
  try {
    return sessionStorage.getItem(doneKey(userId, membershipId)) === "1"
  } catch {
    return false
  }
}

const membershipRoles = membership =>
  membership?.role_types ||
  membership?.roleTypes ||
  membership?.roles ||
  []

/**
 * Pre-accept memberships have empty join profile. After Accept Invite,
 * estimated_hours / description / roles are set — even if role_types are
 * omitted from the invited-project include.
 */
export const membershipNeedsProjectAccept = membership => {
  if (!membership?.id) return false

  const roles = membershipRoles(membership)
  const hasRole = Boolean(
    roles.length > 0 ||
      membership.role_type ||
      (Array.isArray(membership.role_type_ids) &&
        membership.role_type_ids.length > 0),
  )
  const hours = Number(membership.estimated_hours)
  const hasHours = Number.isFinite(hours) && hours > 0
  const hasDescription = Boolean(String(membership.description || "").trim())

  // Any join-profile field present → already completed accept.
  if (hasHours || hasDescription || hasRole) return false
  return true
}

export const mapMandatoryProjectAcceptInvite = (result, userId = null) => {
  if (!result?.id) return null

  const membership = Array.isArray(result.my_membership)
    ? result.my_membership[0]
    : result.my_membership

  if (!membership?.id) return null
  if (isMandatoryProjectAcceptDone(userId, membership.id)) return null
  if (!membershipNeedsProjectAccept(membership)) return null

  return {
    id: String(membership.id),
    projectId: String(result.id),
    name: result.name || result.title || "",
  }
}

/**
 * Returns { id, projectId, name } when the signed-in member must complete
 * the project join modal, otherwise null.
 */
export const fetchMandatoryProjectAcceptInvite = async (user = null) => {
  if (user && !isMemberUser(user)) return null
  if (user && Number(user.total_projects) !== 1) return null

  try {
    const response = await get(endpoints.getInvitedProject, {
      params: { "with[]": ["my_membership.user"] },
      skipErrorToast: true,
    })

    if (!response?.data?.success) return null
    return mapMandatoryProjectAcceptInvite(response.data.result, user?.id)
  } catch {
    return null
  }
}
